<template>
  <div>
    <fieldset class="border p-3">
      <legend class="w-auto bg-facebook px-2 py-1 text-white rounded border h6">CONTRIBUTIONS <b-badge variant="light" class="text-dark">SSS &middot; PHILHEALTH &middot; HDMF</b-badge></legend>

      <!-- Filter -->
      <b-row class="align-items-end mb-3">
        <b-col md="3">
          <label class="small font-weight-bold text-uppercase mb-1">View</label>
          <b-form-select v-model="mode" size="sm" :options="modes" @change="onModeChange"></b-form-select>
        </b-col>
        <b-col md="3" v-if="mode === 'yearly'">
          <label class="small font-weight-bold text-uppercase mb-1">Year</label>
          <b-form-select v-model="year" size="sm" :options="yearOptions" @change="load"></b-form-select>
        </b-col>
        <b-col md="3" v-if="mode === 'monthly'">
          <label class="small font-weight-bold text-uppercase mb-1">Month</label>
          <b-form-input type="month" v-model="month" size="sm" @change="load"></b-form-input>
        </b-col>
        <template v-if="mode === 'custom'">
          <b-col md="3">
            <label class="small font-weight-bold text-uppercase mb-1">From</label>
            <b-form-input type="date" v-model="from" size="sm" @change="load"></b-form-input>
          </b-col>
          <b-col md="3">
            <label class="small font-weight-bold text-uppercase mb-1">To</label>
            <b-form-input type="date" v-model="to" size="sm" @change="load"></b-form-input>
          </b-col>
        </template>
        <b-col class="text-right">
          <small class="text-muted">{{ rangeText }}</small>
        </b-col>
      </b-row>

      <!-- Totals -->
      <b-row class="mb-3">
        <b-col md="3" class="mb-2" v-for="card in cards" :key="card.key">
          <div class="rounded shadow-sm border p-2 d-flex align-items-center">
            <div class="rounded d-flex align-items-center justify-content-center text-white mr-2 contrib-icon" :class="card.color">
              <font-awesome-icon :icon="card.icon" />
            </div>
            <div>
              <small class="text-muted text-uppercase font-weight-bold d-block">{{ card.label }}</small>
              <strong><number-format-component :input_value="Number(totals[card.key] || 0)" /></strong>
            </div>
          </div>
        </b-col>
      </b-row>

      <div class="text-center py-4" v-if="loading"><b-spinner small></b-spinner></div>
      <table v-else class="table table-bordered table-sm mb-0">
        <thead class="thead-dark">
          <tr>
            <th>Payroll Period</th>
            <th>Source</th>
            <th>Project / Group</th>
            <th>Voucher</th>
            <th class="text-right">SSS</th>
            <th class="text-right">PhilHealth</th>
            <th class="text-right">HDMF (Pag-IBIG)</th>
            <th class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in rows" :key="r.source + '-' + r.payroll_id">
            <td><strong>{{ r.period }}</strong></td>
            <td><b-badge :variant="r.source === 'Office' ? 'dark' : 'info'">{{ r.source }}</b-badge></td>
            <td>{{ r.reference || '—' }}</td>
            <td><small>{{ r.voucher_number || '—' }}</small></td>
            <td class="text-right"><number-format-component :input_value="r.sss_payment" /></td>
            <td class="text-right"><number-format-component :input_value="r.philhealth_payment" /></td>
            <td class="text-right"><number-format-component :input_value="r.pagibig_payment" /></td>
            <td class="text-right font-weight-bold"><number-format-component :input_value="r.total" /></td>
          </tr>
          <tr v-if="rows.length === 0">
            <td colspan="8" class="text-center text-muted py-3">No payroll with contributions for this period.</td>
          </tr>
        </tbody>
        <tfoot v-if="rows.length > 0">
          <tr class="bg-dark text-white font-weight-bold">
            <td colspan="4" class="text-right">Total ({{ totals.payrolls }} payroll{{ totals.payrolls === 1 ? '' : 's' }})</td>
            <td class="text-right"><number-format-component :input_value="totals.sss" /></td>
            <td class="text-right"><number-format-component :input_value="totals.philhealth" /></td>
            <td class="text-right"><number-format-component :input_value="totals.pagibig" /></td>
            <td class="text-right"><number-format-component :input_value="totals.total" /></td>
          </tr>
        </tfoot>
      </table>
      <small class="text-muted d-block mt-2">Amounts are the contributions deducted on each saved payroll (site payrolls and office payrolls). Filters apply to the payroll period end date.</small>
    </fieldset>
  </div>
</template>

<script>
  import NumberFormatComponent from '@/views/helpers/NumberFormatComponent.vue';

  export default {
    name: 'contributions',
    components: { NumberFormatComponent },
    props: {
      params: { type: Object, default: () => ({}), required: true },
    },
    data() {
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      return {
        loading: false,
        mode: 'all',
        modes: [
          { value: 'all', text: 'All' },
          { value: 'yearly', text: 'Yearly' },
          { value: 'monthly', text: 'Monthly' },
          { value: 'custom', text: 'Custom date' },
        ],
        year: now.getFullYear(),
        month: now.getFullYear() + '-' + pad(now.getMonth() + 1),
        from: now.getFullYear() + '-01-01',
        to: now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate()),
        years: [],
        rows: [],
        totals: { sss: 0, philhealth: 0, pagibig: 0, total: 0, payrolls: 0 },
        cards: [
          { key: 'sss', label: 'SSS', icon: 'balance-scale', color: 'bg-info' },
          { key: 'philhealth', label: 'PhilHealth', icon: 'check-circle', color: 'bg-success' },
          { key: 'pagibig', label: 'HDMF (Pag-IBIG)', icon: 'cubes', color: 'bg-warning' },
          { key: 'total', label: 'Total', icon: 'chart-line', color: 'bg-dark' },
        ],
      }
    },
    computed: {
      manpowerId() {
        return this.params && this.params.form ? this.params.form.id : null;
      },
      yearOptions() {
        const list = this.years.length ? this.years : [new Date().getFullYear()];
        return list.map(y => ({ value: y, text: String(y) }));
      },
      range() {
        if (this.mode === 'yearly') return { from: this.year + '-01-01', to: this.year + '-12-31' };
        if (this.mode === 'monthly' && this.month) {
          const [y, m] = this.month.split('-').map(Number);
          const last = new Date(y, m, 0).getDate();
          return { from: this.month + '-01', to: this.month + '-' + String(last).padStart(2, '0') };
        }
        if (this.mode === 'custom') return { from: this.from, to: this.to };
        return { from: null, to: null };
      },
      rangeText() {
        if (!this.range.from) return 'All payroll periods';
        return 'Periods ending ' + this.range.from + ' to ' + this.range.to;
      }
    },
    watch: {
      manpowerId(id) {
        if (id && id !== -1) this.load();
      }
    },
    created() {
      if (this.manpowerId && this.manpowerId !== -1) this.load();
    },
    methods: {
      onModeChange() {
        if (this.mode === 'yearly' && this.years.length && this.years.indexOf(this.year) === -1) this.year = this.years[0];
        this.load();
      },
      load() {
        if (!this.manpowerId || this.manpowerId === -1) return;
        if (this.mode === 'custom' && (!this.from || !this.to)) return;
        this.loading = true;
        this.axios.get('maintenance/manpowers/contributions', { params: { id: this.manpowerId, from: this.range.from, to: this.range.to } })
          .then(response => {
            this.rows = response.data.rows || [];
            this.totals = response.data.totals || this.totals;
            this.years = response.data.years || [];
            this.loading = false;
          })
          .catch(error => {
            console.log(error);
            this.loading = false;
          });
      }
    }
  }
</script>

<style scoped>
  .contrib-icon {
    width: 34px;
    height: 34px;
    flex: 0 0 34px;
  }
</style>
